import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { assessmentsAPI, intelligenceAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { useAssessmentSecurity } from '../hooks/useAssessmentSecurity';
import { useAssessmentTimer } from '../hooks/useAssessmentTimer';
import { AssessmentIntro } from '../components/assessment/AssessmentIntro';
import { AssessmentQuestion } from '../components/assessment/AssessmentQuestion';

const fullScreenMsg = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F1EAE1', color: 'hsl(var(--muted-foreground))' };

export default function TakeAssessmentPage() {
  const { accessCode } = useParams();

  const [assessment, setAssessment] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [timePerQuestion, setTimePerQuestion] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [showAccommodation, setShowAccommodation] = useState(false);

  const attemptIdRef = useRef(null);
  const attemptDbIdRef = useRef(null);
  const handleSubmitRef = useRef(null);

  const { antiCheatCounts, showWarning, setShowWarning, warningMsg, keystrokesRef } =
    useAssessmentSecurity({ started, submitted, attemptIdRef, currentQ });

  const { timeLeft, setTimeLeft, clearTimer } = useAssessmentTimer({
    started, submitted, onExpire: () => handleSubmitRef.current?.(),
  });

  // Load assessment by access code
  const loadAssessment = useCallback(async () => {
    try {
      const res = await assessmentsAPI.getByCode(accessCode);
      setAssessment(res.data);

      if (res.data.existing_attempt && (res.data.existing_attempt.status === 'completed' || res.data.existing_attempt.status === 'scored')) {
        setAttempt(res.data.existing_attempt);
        setSubmitted(true);
        setLoading(false);
        return;
      }

      setAnswers(new Array(res.data.questions.length).fill(''));
      setTimePerQuestion(new Array(res.data.questions.length).fill(0));
      setTimeLeft((res.data.time_limit_minutes || 30) * 60);
    } catch (err) {
      toast.error('Assessment not found or no longer active');
    } finally {
      setLoading(false);
    }
  }, [accessCode, setTimeLeft]);

  // Auto-refresh if assessment is still generating (no questions yet)
  useEffect(() => {
    if (!assessment || !assessment.questions || assessment.questions.length > 0 || started) return;
    const iv = setInterval(loadAssessment, 5000);
    return () => clearInterval(iv);
  }, [assessment, started, loadAssessment]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  const handleStart = async () => {
    if (!candidateName.trim() || !candidateEmail.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    try {
      const res = await assessmentsAPI.startAttempt(assessment._id, {
        candidate_name: candidateName,
        candidate_email: candidateEmail
      });
      setAttempt(res.data);
      // Candidate ops (save answer / anti-cheat / submit) authorize with the
      // access token — NOT the MongoDB id. The db id is kept only for linking
      // biometric keystroke data to this session.
      attemptIdRef.current = res.data.attempt_access_token;
      attemptDbIdRef.current = res.data.id;
      setStarted(true);
      setQuestionStartTime(Date.now());
      toast.success('Assessment started. Good luck!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start assessment');
    }
  };

  const saveCurrentAnswer = async () => {
    if (!attemptIdRef.current) return;
    const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
    const newTPQ = [...timePerQuestion];
    newTPQ[currentQ] = (newTPQ[currentQ] || 0) + timeSpent;
    setTimePerQuestion(newTPQ);
    try {
      await assessmentsAPI.saveAnswer(attemptIdRef.current, {
        question_index: currentQ,
        answer: answers[currentQ] || '',
        time_spent: newTPQ[currentQ],
        current_question: currentQ
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to save answer:', err);
    }
  };

  const goToQuestion = async (idx) => {
    await saveCurrentAnswer();
    setCurrentQ(idx);
    setQuestionStartTime(Date.now());
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    clearTimer();
    await saveCurrentAnswer();
    try {
      await assessmentsAPI.submitAttempt(attemptIdRef.current);
      intelligenceAPI.submitBiometrics({
        session_type: 'assessment',
        session_id: attemptDbIdRef.current,
        candidate_email: candidateEmail,
        keystrokes: keystrokesRef.current.slice(-200),
        face_captures: [],
      }).catch(err => {
        if (process.env.NODE_ENV === 'development') console.error('Biometric submit failed:', err);
      });
      toast.success('Assessment submitted! You can close this tab.');
      setTimeout(() => {
        window.close();
        setTimeout(() => { if (!document.hidden) window.location.href = '/portal'; }, 500);
      }, 1500);
    } catch (err) {
      setSubmitted(false);
      toast.error('Failed to submit. Please try again.');
    }
  };
  handleSubmitRef.current = handleSubmit;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const runPython = async (code, language) => {
    const { data } = await assessmentsAPI.runCode(attemptIdRef.current, { code, language });
    return data;
  };

  if (loading) return <div style={fullScreenMsg}>Loading assessment...</div>;
  if (!assessment) return <div style={fullScreenMsg}>Assessment not found</div>;

  if (submitted) {
    const alreadyCompleted = attempt && (attempt.status === 'scored' || attempt.status === 'completed') && attempt.started_at !== attempt.submitted_at;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F1EAE1' }}>
        <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '3rem', textAlign: 'center', maxWidth: 480 }}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 1.5rem' }}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#002A38', marginBottom: '0.75rem' }}>
            {alreadyCompleted ? 'You have already completed this assessment' : 'Assessment Submitted!'}
          </h2>
          <p style={{ fontSize: '0.9375rem', color: '#64748B', marginBottom: '1rem', lineHeight: 1.6 }}>
            {alreadyCompleted
              ? 'This assessment has already been completed. You cannot retake it.'
              : 'Your answers are being scored by AI. The recruiter will review your results shortly.'}
          </p>
          {!attempt?.started_at || attempt?.started_at === attempt?.submitted_at ? (
            <p style={{ fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '1.5rem' }}>
              You answered {answers.filter(a => a && a.trim()).length} of {assessment.questions.length} questions.
            </p>
          ) : null}
          <Button onClick={() => { window.close(); setTimeout(() => window.location.href = '/portal', 500); }} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, fontSize: '0.9375rem', padding: '0.75rem 2rem' }}>
            {alreadyCompleted ? 'Close This Tab' : 'Back to My Portal'}
          </Button>
        </Card>
      </div>
    );
  }

  if (!started) {
    return (
      <AssessmentIntro
        assessment={assessment}
        accessCode={accessCode}
        candidateName={candidateName}
        setCandidateName={setCandidateName}
        candidateEmail={candidateEmail}
        setCandidateEmail={setCandidateEmail}
        onStart={handleStart}
        onRefresh={loadAssessment}
        showAccommodation={showAccommodation}
        setShowAccommodation={setShowAccommodation}
      />
    );
  }

  return (
    <AssessmentQuestion
      assessment={assessment}
      currentQ={currentQ}
      answers={answers}
      setAnswers={setAnswers}
      timeLeft={timeLeft}
      formatTime={formatTime}
      antiCheatCounts={antiCheatCounts}
      goToQuestion={goToQuestion}
      onSubmit={handleSubmit}
      showWarning={showWarning}
      setShowWarning={setShowWarning}
      warningMsg={warningMsg}
      runPython={runPython}
    />
  );
}
